import { UIChunkType } from '@myre/app-agent-domain';
import { ChatConfigApi } from '@myre/app-agent-generated/sdk/angular/ChatConfigApi';
import { FileApi } from '@myre/app-agent-generated/sdk/angular/FileApi';
import { type Agent } from '@myre/app-agent-generated/types/Agent';
import { type ChatConfigResponse } from '@myre/app-agent-generated/types/ChatConfigResponse';
import { type ChatFileAttachment } from '@myre/app-agent-generated/types/ChatFileAttachment';
import { type ChatRequest } from '@myre/app-agent-generated/types/ChatRequest';
import { MessageRole } from '@myre/app-agent-generated/types/MessageRole';
import { ADMIN_USER_IDS } from '@myre/common-permission';
import {
  joinTranslated,
  NO_TEXT,
  type Translated,
  verbatim,
} from '@myre/common-i18n';
import { TagType } from '@myre/common-tag';
import {
  type SystemI18n,
  type UiI18n,
  type UiLibI18n,
} from '@myre/domain-i18n-wordings';
import { downloadFromUrls, DynamicEnvService } from '@myre/ui-domain-http';
import { BannerComponent, TagComponent } from '@myre/ui-primitive-components';
import { LANGLOCALE } from '@myre/ui-primitive-core';
import { type ISelectItem } from '@myre/ui-primitive-forms';
import { TooltipDirective } from '@myre/ui-primitive-tooltip-directive';

import { Chat } from '@ai-sdk/angular';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  ErrorHandler,
  inject,
  input,
  output,
  signal,
  untracked,
  ViewChild,
} from '@angular/core';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import { select, Store } from '@ngrx/store';
import {
  DefaultChatTransport,
  generateId,
  type ChatStatus,
  type FileUIPart,
} from 'ai';
import { capitalize } from 'lodash-es';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  filter,
  firstValueFrom,
  map,
  of,
  retry,
  shareReplay,
  switchMap,
  take,
  takeUntil,
  timer,
} from 'rxjs';
import { type TupleToUnion } from 'type-fest';

import { AbstractChatAnalyticsService } from '../analytics/abstract-chat-analytics.service';
import { getImgSource } from '../helpers/images.helpers';
import { WORDINGS } from '../myre-wordings-loader';
import { loggedUserSelector } from '../services/auth/auth.service';

import { ChatInputComponent } from './components/chat-input/chat-input.component';
import { ChatMessageComponent } from './components/chat-message/chat-message.component';
import { UnreadIndicatorComponent } from './components/unread-indicator/unread-indicator.component';
import { WelcomeScreenComponent } from './components/welcome-screen/welcome-screen.component';
import { AutoScrollDirective } from './directives/auto-scroll.directive';
import {
  approvalChoice,
  clarificationChoice,
  navigateChoice,
  type CardChoice,
} from './helpers/approval-resume';
import {
  buildChatContextItems,
  contextItemText,
  contextKeyLabel,
} from './helpers/context-items';
import {
  type ApprovalDecision,
  type ChatActivityStatus,
  type ChatContextItem,
  type ChatInputConfig,
  type ChatWelcomeConfig,
  type ClarificationAnswer,
  type MyreUiMessage,
} from './interfaces/chat.interfaces';
import { AgentMemoryModalService } from './services/agent-memory-modal.service';
import { AgentStatusService } from './services/agent-status.service';
import { AbstractChatContextNamesService } from './services/chat-context-names.service';
import { ChatContextService } from './services/chat-context.service';
import { ChatSessionService } from './services/chat-session.service';
import { MessageTimestampService } from './services/message-timestamp.service';

/**
 * ChatComponent - Orchestrator for the chat UI
 *
 * This component coordinates the interaction between:
 * - MessageTimestampService: Handles timestamp assignment and formatting
 * - AgentStatusService: Manages agent status display logic
 * - AutoScrollDirective: Handles scroll behavior
 * - Child components: ChatMessageComponent, ChatInputComponent, etc.
 */
@Component({
  selector: 'app-chat',
  standalone: true,
  imports: [
    BannerComponent,
    TagComponent,
    TooltipDirective,
    ChatMessageComponent,
    ChatInputComponent,
    UnreadIndicatorComponent,
    WelcomeScreenComponent,
    AutoScrollDirective,
  ],
  templateUrl: './chat.component.html',
  styleUrl: './chat.component.scss',
})
export class ChatComponent {
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _errorHandler = inject(ErrorHandler);
  private readonly _dynamicEnvService = inject(DynamicEnvService);
  private readonly _langLocale = inject(LANGLOCALE);
  private readonly _messageTimestampService = inject(MessageTimestampService);
  private readonly _agentMemoryModalService = inject(AgentMemoryModalService);
  private readonly _agentStatusService = inject(AgentStatusService);
  private readonly _chatConfigApi = inject(ChatConfigApi);
  private readonly _fileApi = inject(FileApi);
  private readonly _chatContextService = inject(ChatContextService);
  private readonly _abstractChatContextNamesService = inject(
    AbstractChatContextNamesService,
    { optional: true },
  );

  private readonly _abstractChatAnalyticsService = inject(
    AbstractChatAnalyticsService,
    { optional: true },
  );

  private readonly _chatSessionService = inject(ChatSessionService);
  private readonly _store$ = inject(Store);
  private readonly _wordings = inject<UiI18n & SystemI18n & UiLibI18n>(
    WORDINGS,
  );

  protected get chatWordings() {
    return this._wordings._library.chat;
  }

  @ViewChild(AutoScrollDirective)
  private readonly _autoScrollDirective?: AutoScrollDirective;

  @ViewChild(ChatInputComponent)
  private readonly _chatInputComponent?: ChatInputComponent;

  /**
   * Whether this chat instance is currently visible.
   * Controls API config fetching (prevents calls when hidden/minimized).
   * Defaults to true for backward compatibility.
   */
  public readonly visible = input(true);

  /**
   * If set, the component loads this session on init.
   */
  public readonly initialSessionId = input<string>();

  /** Observable of chat visibility state */
  private readonly _chatOpened$ = toObservable(this.visible);

  /**
   * Fetch config from API based on current context.
   * Only fetches when:
   * 1. Chat window is visible (prevents unnecessary calls when minimized)
   * 2. Context has actually changed (deduplication via contextForApi$)
   *
   * When the window is hidden, API calls are not made. When it becomes
   * visible, the latest context is used to fetch the config.
   */
  private readonly _apiConfigFromServer$ = combineLatest([
    this._chatOpened$,
    this._chatContextService.contextForApi$,
  ]).pipe(
    // Only proceed when chat is open
    filter(([isOpen]) => isOpen),
    // Extract context and deduplicate (in case opened state changes but context doesn't)
    distinctUntilChanged(
      ([, prevContext], [, currContext]) => prevContext === currContext,
    ),
    // Skip agent calls on non-customer routes (e.g. /admin/*)
    filter(([, context]) => !!context.params.customerId),
    // Fetch config with the current context. catchError/retry stay INSIDE the
    // switchMap so a transient getConfig failure only drops that one fetch (and
    // keeps the last good config) — it must not error the outer stream, which
    // would complete it and never refetch (agent dropdown gone for good).
    switchMap(([, context]) =>
      this._chatConfigApi.getConfig(context).pipe(
        retry({ count: 2, delay: 1000 }),
        catchError(() => EMPTY),
      ),
    ),
    shareReplay(1),
  );

  private readonly _apiConfigFromServer = toSignal(this._apiConfigFromServer$);

  /**
   * API configuration override (for Storybook or manual configuration)
   * If not provided, config is fetched from ChatConfigApi
   */
  public readonly apiConfigOverride = input<ChatConfigResponse>(undefined, {
    alias: 'apiConfig',
  });

  /**
   * Welcome screen icon URL override (displayed above greeting message)
   * If not provided, defaults to the Myre colored logo
   */
  public readonly welcomeIconUrl = input<string>();

  /**
   * Welcome screen icon emoji fallback
   */
  public readonly welcomeIcon = input<string>();

  // Default welcome icon URL using the same approach as footer-minimalist
  private readonly _defaultWelcomeIconUrl = getImgSource({
    type: 'logo',
    name: 'minimal-colored',
  });

  protected readonly _welcomeIconUrl = computed(
    () => this.welcomeIconUrl() || this._defaultWelcomeIconUrl,
  );

  /**
   * Emitted when the user clicks the new chat button
   */
  public readonly newChatRequested = output<void>();

  /**
   * Emitted when the chat title should change (first user message sent).
   * Used by ChatWindowComponent to update the window tab title.
   */
  public readonly titleChanged = output<string>();

  /**
   * Emitted when the activity hint changes, so a minimized window can flag
   * that the agent is thinking or that a reply has not been seen yet.
   */
  public readonly statusChanged = output<ChatActivityStatus>();

  // Computed: merged API config (override takes precedence over server config)
  protected readonly _apiConfig = computed<ChatConfigResponse | undefined>(
    () => this.apiConfigOverride() ?? this._apiConfigFromServer(),
  );

  protected readonly _selectedAgentName = signal<string>('');

  protected readonly _agents = computed<readonly Agent[]>(
    () => this._apiConfig()?.agents ?? [],
  );

  protected readonly _selectedAgent = computed<Agent | undefined>(() => {
    const agents = this._agents();
    const selectedName = this._selectedAgentName();

    // Find by name, or find default, or take first
    return (
      agents.find(a => a.name === selectedName) ||
      agents.find(a => a.isDefault) ||
      agents[0]
    );
  });

  // Computed: selected agent name for dropdown display
  // Uses the resolved agent name (handles default fallback)
  protected readonly _selectedAgentNameForDropdown = computed(
    () => this._selectedAgent()?.name ?? '',
  );

  protected readonly _agentDropdownItems = computed<ISelectItem<string>[]>(
    () => {
      return this._agents().map(agent => {
        const name = verbatim(capitalize(agent.name));

        return {
          label: agent.icon
            ? joinTranslated([verbatim(agent.icon), name], ' ')
            : name,
          value: agent.name,
        };
      });
    },
  );

  protected readonly _welcomeConfig = computed<ChatWelcomeConfig | undefined>(
    () => {
      const agent = this._selectedAgent();
      const apiConfig = this._apiConfig();

      const message = apiConfig?.greetingMessage || '';

      if (!message) {
        return undefined;
      }

      return {
        message,
        iconUrl: this._welcomeIconUrl(),
        icon: this.welcomeIcon(),
        suggestions: agent?.suggestions,
      };
    },
  );

  protected readonly _inputConfig = computed<ChatInputConfig>(() => {
    const agent = this._selectedAgent();

    const hasMessages =
      !!this.initialSessionId() || this._messages().length > 0;
    const placeholder = hasMessages
      ? this.chatWordings.replyPlaceholder()
      : this.chatWordings.askMyrePlaceholder();

    return {
      placeholder: agent?.inputPlaceholder || placeholder,
      enableUpload: agent?.acceptUpload,
      acceptedFileTypes: agent?.acceptUploadFileTypes?.join(','),
      mcpServers: agent?.mcpServers,
      isAdmin: this._isAdmin(),
    };
  });

  // Enabled tools (MCP server IDs selected by the user)
  protected readonly _enabledTools = signal<string[]>([]);

  private readonly _loggedUser$ = this._store$.pipe(select(loggedUserSelector));
  private readonly _loggedUser = toSignal(this._loggedUser$);

  protected readonly _isAdmin = computed(() => {
    const user = this._loggedUser();

    return (
      !!user &&
      ADMIN_USER_IDS.includes(user.id as TupleToUnion<typeof ADMIN_USER_IDS>)
    );
  });

  private readonly _debugEnabled = signal(false);

  // LLM title once the server has produced it (overrides the placeholder)
  private readonly _serverTitle = signal<string | null>(null);

  // Guards the one-time server-title poll (a subscription) per session
  private _titlePollStarted = false;

  // Window tab title: the server title if available, else a placeholder derived
  // from the first user message (its display label, or its truncated text).
  protected readonly _windowTitle = computed<string | null>(() => {
    const server = this._serverTitle();

    if (server) {
      return server;
    }

    const firstUserMessage = this._messages().find(m => m.role === 'user');

    if (!firstUserMessage) {
      return null;
    }

    const source =
      firstUserMessage.metadata?.displayLabel ??
      firstUserMessage.parts
        .filter((p): p is { type: 'text'; text: string } => p.type === 'text')
        .map(p => p.text)
        .join(' ');

    return source ? this._truncateTitle(source) : null;
  });

  // Completed assistant replies — excludes the one still streaming, so a reply
  // only counts as "arrived" once it is done (the in-progress one shows as
  // `processing`, not yet `unread`).
  private readonly _completedReplies = computed(() => {
    const count = this._messages().filter(m => m.role === 'assistant').length;

    return this._chatStatus() === 'streaming' ? Math.max(0, count - 1) : count;
  });

  // Replies seen by the user — kept in sync while the window is visible, so a
  // reply that completes while hidden leaves completed > seen (unread)
  private readonly _seenReplies = signal(0);

  // Whether the last assistant turn ended by asking a clarification question
  private readonly _endedWithQuestion = computed(() => {
    const last = this._messages().at(-1);

    return (
      last?.role === 'assistant' &&
      last.parts.some(part => part.type === UIChunkType.DataQuestion)
    );
  });

  // Customer ID at the time the current chat session started
  private readonly _sessionCustomerId = signal<number | null>(null);

  // Warning: current customer differs from the session's customer
  protected readonly _showCustomerMismatchWarning = computed(() => {
    const sessionCustomerId = this._sessionCustomerId();

    if (!sessionCustomerId || !this._messages().length) {
      return false;
    }

    const currentCustomerId =
      this._chatContextService.context().params.customerId;

    return currentCustomerId !== sessionCustomerId;
  });

  // Computed: whether to show the welcome screen (no messages yet)
  protected readonly _showWelcomeScreen = computed(
    () => !this._messages().length && this._chatStatus() === 'ready',
  );

  /**
   * Computed: the ids in scope, named in the product's vocabulary.
   * Uses the reactive context signal which automatically updates on navigation
   * when context is enabled, ensuring the context zone reflects the current route.
   */
  protected readonly _contextItems = computed<ChatContextItem[]>(() =>
    buildChatContextItems({
      contextParams: this._chatContextService.context().params,
      names: this._abstractChatContextNamesService?.names() ?? {},
      excludedKeys: this._chatContextService.excludedKeys(),
      keyPath: this._chatContextService.contextKeyPath(),
      label: key => contextKeyLabel(key, this.chatWordings.context),
    }),
  );

  // Computed: whether to show the context zone
  // Only show when enabled AND there are displayable context items (excluding hidden keys)
  protected readonly _showContextZone = computed(
    () => this._chatContextService.enabled() && this._contextItems().length > 0,
  );

  /**
   * The page the user unfolded the ancestors on: unfolding says "show me this
   * page's whole scope", so another page opens folded again, as it reads its own
   * removals.
   */
  private readonly _unfoldedPage = signal<string | null>(null);

  protected readonly _contextUnfolded = computed(
    () => this._unfoldedPage() === this._chatContextService.pageKey(),
  );

  /** The ancestors of the page's own entity — folded unless asked for. */
  protected readonly _parentContextItems = computed(() =>
    this._contextItems().slice(0, -1),
  );

  protected readonly _parentContextCount = computed(
    () => this._parentContextItems().length,
  );

  /** Folded, the toggle counts the hidden ancestors; unfolded, it shows none. */
  protected readonly _parentContextBadge = computed(() =>
    this._contextUnfolded()
      ? NO_TEXT
      : this.chatWordings.parentContextCount({
          bindings: { count: this._parentContextCount() },
        }),
  );

  /** Every chip of the zone reads the same way as the others in the app. */
  protected readonly _contextTagType = TagType.Tertiary;

  protected _contextItemText(item: ChatContextItem): Translated {
    return contextItemText(item, this.chatWordings);
  }

  protected readonly _shownContextItems = computed(() =>
    this._contextUnfolded()
      ? this._contextItems()
      : this._contextItems().slice(-1),
  );

  protected readonly _contextToggleLabel = computed(() =>
    this._contextUnfolded()
      ? this.chatWordings.hideParentContext()
      : this.chatWordings.showParentContext(),
  );

  // Fixed ID for error message timestamp
  protected readonly _errorMessageId = 'chat-error-message';

  // Error message details (for title attribute)
  protected readonly _errorDetails = signal<string | null>(null);

  protected readonly _errorMessage = computed<MyreUiMessage>(() => ({
    id: this._errorMessageId,
    role: 'assistant',
    parts: [{ type: 'text', text: this.chatWordings.somethingWentWrong() }],
  }));

  // Pending files to be sent with the next message
  private readonly _pendingFiles = signal<File[]>([]);

  // Display-only file parts per message ID (not sent to the SDK)
  private readonly _filePartsMap = signal<Map<string, FileUIPart[]>>(new Map());

  // Chat instance
  protected readonly _chat = new Chat<MyreUiMessage>({
    onError: error => this._handleError(error),
    transport: new DefaultChatTransport({
      credentials: 'include',
      body: () =>
        ({
          context: this._chatContextService.contextForApi(),
          mode: this._selectedAgent()?.name,
          enabledTools: this._enabledTools().length
            ? this._enabledTools()
            : undefined,
          debugAgent: this._debugEnabled() || undefined,
        }) satisfies Omit<ChatRequest, 'id' | 'message' | 'files'>,
      // Only the turn's message travels. The server owns the conversation and
      // rebuilds the model's history from what it persisted, so the transcript
      // this component holds is for display, not something to echo back.
      prepareSendMessagesRequest: ({ id, messages, body }) => ({
        body: { ...body, id, message: messages.at(-1) },
      }),
      headers: {
        Lang: this._langLocale,
      },
      api: `${this._dynamicEnvService.env.myre.agent.back.host}/chat`,
      fetch: (url, init) => {
        const customerId = this._chatContextService.context().params.customerId;
        const rewrittenUrl = new URL(url as string);

        rewrittenUrl.pathname = `/customers/${customerId}/chat`;

        return this._fetchWithFiles(rewrittenUrl, init);
      },
    }),
  });

  // Unread messages count
  protected readonly _unreadCount = signal(0);

  // "Don't ask again" for navigation confirms, bound to the current chat: once
  // set, navigate proposals route immediately without a confirm card. Reset on
  // a new conversation.
  protected readonly _skipNavConfirm = signal(false);

  // Computed: messages from chat
  // Note: We create new object references to ensure Angular detects changes
  // when message parts are mutated during streaming (text appended to parts).
  // File parts from _filePartsMap are injected here for display only,
  // keeping _chat.messages clean so the SDK doesn't serialize them.
  protected readonly _messages = computed<MyreUiMessage[]>(() => {
    const filePartsMap = this._filePartsMap();

    return this._chat.messages.map(message => {
      const extraFileParts = filePartsMap.get(message.id) ?? [];

      return {
        ...message,
        parts: [...message.parts.map(part => ({ ...part })), ...extraFileParts],
      };
    });
  });

  protected readonly _chatStatus = computed<ChatStatus>(
    () => this._chat.status,
  );

  private readonly _lastAssistantMessage = computed(() => {
    const last = this._messages().at(-1);

    return last?.role === 'assistant' ? last : null;
  });

  protected readonly _shouldShowStatus = computed(() =>
    this._agentStatusService.shouldShowStatus(
      this._lastAssistantMessage(),
      this._chat.status === 'streaming',
    ),
  );

  protected readonly _currentStatusText = computed(() =>
    this._agentStatusService.extractStatusText(this._lastAssistantMessage()),
  );

  protected readonly _isLastMessageStreaming = computed(
    () => this._chatStatus() === 'streaming',
  );

  protected readonly _hintStatus = computed<ChatActivityStatus>(() => {
    if (this._chatStatus() === 'streaming') {
      return 'processing';
    }

    if (this._completedReplies() > this._seenReplies()) {
      return this._endedWithQuestion() ? 'question' : 'unread';
    }

    return 'idle';
  });

  constructor() {
    // Effect to process timestamps and handle scroll updates
    effect(() => {
      const messages = this._messages();
      const currentStatus = this._chat.status;

      this._messageTimestampService.processMessages(messages, currentStatus);

      // Schedule scroll update
      if (messages.some(message => message.parts.length > 0)) {
        setTimeout(() => this._autoScrollDirective?.handleContentChange(), 0);
      }
    });

    // Auto-focus the input when the window becomes visible
    effect(() => {
      if (this.visible()) {
        setTimeout(() => this._chatInputComponent?.focus());
      }
    });

    // Load a historical session if initialSessionId is provided (once)
    let initialLoaded = false;

    effect(() => {
      const sessionId = this.initialSessionId();

      if (sessionId && !initialLoaded) {
        initialLoaded = true;
        // untracked keeps reads inside _onSessionSelected (e.g. the shared
        // activeSessionId signal) from becoming dependencies of this effect,
        // which would otherwise cross-trigger between windows.
        untracked(() => this._onSessionSelected(sessionId));
      }
    });

    // While the window is visible, every completed reply counts as seen — so
    // unread only builds up while it is minimized.
    effect(() => {
      if (this.visible()) {
        this._seenReplies.set(this._completedReplies());
      }
    });

    // Relay the activity hint to the parent window.
    effect(() => this.statusChanged.emit(this._hintStatus()));

    // Emit the window title whenever it changes (placeholder → server title).
    effect(() => {
      const title = this._windowTitle();

      if (title) {
        this.titleChanged.emit(title);
      }
    });

    let previousStatus: ChatStatus = 'ready';

    effect(() => {
      const currentStatus = this._chatStatus();

      if (
        (previousStatus === 'streaming' || previousStatus === 'submitted') &&
        currentStatus === 'ready'
      ) {
        const lastAssistant = [...this._messages()]
          .reverse()
          .find(m => m.role === 'assistant');

        this._abstractChatAnalyticsService?.trackResponseEnded({
          agent: this._selectedAgent()?.name,
          partsCount: lastAssistant?.parts.length ?? 0,
        });
      }

      previousStatus = currentStatus;
    });
  }

  /**
   * Returns all messages formatted as Markdown.
   */
  public getMessagesAsMarkdown(): string {
    return this._messages()
      .map(message => {
        const role = message.role === 'user' ? '**User**' : '**Assistant**';
        const texts = message.parts
          .filter((p): p is { type: 'text'; text: string } => p.type === 'text')
          .map(p => p.text);

        return `${role}\n\n${texts.join('\n\n')}`;
      })
      .join('\n\n---\n\n');
  }

  /**
   * Deletes the current active session and clears the chat.
   */
  public deleteCurrentSession(): void {
    const sessionId = this._chatSessionService.activeSessionId();

    if (sessionId) {
      this._onSessionDeleted(sessionId);
    }
  }

  /**
   * Gets the formatted timestamp for a message.
   */
  protected _getFormattedTimestamp(messageId: string): string | null {
    return this._messageTimestampService.formatTimestamp(messageId);
  }

  /**
   * Handles when files are selected from the input component.
   */
  protected _onFilesSelected(files: readonly File[]): void {
    this._pendingFiles.update(current => [...current, ...files]);
  }

  /**
   * Handles when a message is sent from the input component.
   */
  protected _onMessageSent(text: string): void {
    const isFirstMessage = this._messages().length === 0;
    const fileCount = this._pendingFiles().length;

    this._abstractChatAnalyticsService?.trackMessageSent({
      fileCount,
      isFirstMessage,
      agent: this._selectedAgent()?.name,
      messageLength: text.length,
      hasFiles: fileCount > 0,
    });

    this._send(text);
  }

  /**
   * A clarification answer RESUMES the suspended run (the agent asked via a
   * `generate_question` interrupt) — it is NOT a new turn. The answer rides the
   * `resume` channel and shows as a display-only user bubble (its clean label).
   */
  protected _onQuestionAnswered(answer: ClarificationAnswer): void {
    this._sendCardChoice(clarificationChoice(answer));
  }

  /**
   * A navigation decision is the NEXT turn, not a resume: the link was rendered
   * beside a delivered answer, so there is no suspended run to continue. The
   * client-side navigation already happened in the card; what is sent is the
   * outcome sentence ("j'ai ouvert la page « X »"), which the agent reads as the
   * message it is — while the bubble shows the short button label.
   *
   * It still rides the `navigateDecision` channel so the server records the card
   * as decided and a reload renders it settled rather than re-armed.
   */
  protected _onNavigateDecided(answer: ClarificationAnswer): void {
    this._sendCardChoice(navigateChoice(answer));
  }

  /**
   * Resumes a graph suspended on a destructive-commit `interrupt()`. Replays the
   * plan-bound `approvalToken` through the `resume` body channel on the SAME chat
   * session; the server resumes the session's durable thread with
   * `Command({ resume })`. The action's own confirm label (from the plan, e.g.
   * "Envoi confirmé" / "Modification confirmée") rides as the human's choice —
   * the token itself never leaves the `resume` body. On Cancel this never fires
   * and the suspended graph is left un-resumed (no commit; GC'd by the
   * approval-window TTL).
   */
  protected _onApprovalConfirmed(decision: ApprovalDecision): void {
    const approvalPart = this._messages()
      .flatMap(m => m.parts)
      .find(
        p =>
          p.type === 'data-approval' &&
          p.data.approval.approvalToken === decision.approvalToken,
      );

    const actionType =
      approvalPart && approvalPart.type === 'data-approval'
        ? approvalPart.data.approval.kind
        : undefined;

    this._abstractChatAnalyticsService?.trackApprovalAcknowledged({
      actionType,
      agent: this._selectedAgent()?.name,
    });
    this._sendCardChoice(approvalChoice(decision));
  }

  /**
   * The user cancelled the approval card. The graph is never resumed (no commit),
   * but record the cancellation server-side so a reload renders the card cancelled
   * rather than re-arming its buttons. Display-only, fire-and-forget.
   */
  protected _onApprovalCancelled(): void {
    this._abstractChatAnalyticsService?.trackApprovalCancelled({
      agent: this._selectedAgent()?.name,
    });

    const sessionId = this._chatSessionService.activeSessionId();

    if (!sessionId) {
      return;
    }

    this._chatSessionService
      .cancelApproval(sessionId)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe();
  }

  /**
   * Send the human's choice on a card — an approval Confirm, a clarification
   * option, a navigation link.
   *
   * The choice rides the `resume` body, and what the server does with it depends
   * on whether anything is still WAITING. A card that suspended the turn
   * (approval, clarification) is continued on the SAME durable thread via
   * `Command({ resume })`, and `text` never reaches the LLM, the graph state nor
   * `chat_history`. A card that suspended nothing — the navigation link, rendered
   * beside a delivered answer — has no run to continue, so the server reads the
   * request as the ordinary turn it is and `text` IS what the agent answers.
   * Hence the two fields: `text` is what the agent may read, `displayLabel` is
   * the bubble the user sees.
   *
   * Either way it goes out as a normal user message — NOT `sendMessage(undefined)`,
   * which `ai@6` streams INTO the assistant question message (it continues
   * `lastMessage` whenever that is an assistant). That would re-render the
   * answered question card and suppress the processing indicator. A fresh user
   * message keeps `lastMessage` a user turn, so the continuation streams as a
   * fresh assistant message.
   */
  private _sendCardChoice(choice: CardChoice): void {
    const { body, text, displayLabel } = choice;

    if (this._chat.status === 'error') {
      this._chat.clearError();
      this._errorDetails.set(null);
    }

    // Seed the session customer like `_send` does, so the customer-mismatch guard
    // stays armed even if a card choice is ever the first interaction on a session.
    if (!this._sessionCustomerId()) {
      this._sessionCustomerId.set(
        this._chatContextService.context().params.customerId,
      );
    }

    void this._chat.sendMessage({ text, metadata: { displayLabel } }, { body });

    this._autoScrollDirective?.resetOnSend();
    this._unreadCount.set(0);
  }

  private _send(text: string, displayLabel?: string): void {
    if (this._chat.status === 'error') {
      this._chat.clearError();
      this._errorDetails.set(null);
    }

    if (!this._sessionCustomerId()) {
      this._sessionCustomerId.set(
        this._chatContextService.context().params.customerId,
      );
    }

    void this._chat.sendMessage({
      text,
      metadata: displayLabel ? { displayLabel } : undefined,
    });

    // The server generates the LLM title in parallel from this first message —
    // start polling for it right away (fresh sessions only). The window title
    // itself is derived (placeholder → server title) and emitted by an effect.
    if (!this._titlePollStarted && !this.initialSessionId()) {
      this._titlePollStarted = true;
      this._pullServerTitle();
    }

    this._autoScrollDirective?.resetOnSend();
    this._unreadCount.set(0);
  }

  /**
   * Handles stop request from the input component.
   */
  protected _onStopRequested(): void {
    void this._chat.stop();
  }

  /**
   * Handles suggestion selection from the welcome screen.
   */
  protected _onSuggestionSelected(text: string): void {
    this._abstractChatAnalyticsService?.trackSuggestionClicked({
      agent: this._selectedAgent()?.name,
      suggestionLength: text.length,
    });
    this._chatInputComponent?.fillInput(text);
  }

  /**
   * Handles file download request from a message component.
   */
  protected _onFileDownload(file: FileUIPart): void {
    downloadFromUrls(
      this._dynamicEnvService.env.myre.agent.back.host + file.url,
    );
  }

  /**
   * Opens the trace visualizer in a new tab.
   */
  protected _onTraceOpen(traceId: string): void {
    window.open(
      `${this._dynamicEnvService.env.myre.agent.back.host}/trace/${traceId}`,
      '_blank',
    );
  }

  /**
   * Handles scroll to bottom request from the unread indicator.
   */
  protected _onScrollToBottom(): void {
    this._autoScrollDirective?.scrollToBottom();
    this._unreadCount.set(0);
  }

  /**
   * Handles unread count changes from the auto-scroll directive.
   */
  protected _onUnreadCountChange(count: number): void {
    this._unreadCount.set(count);
  }

  /**
   * Handles new chat button click.
   * Stops any ongoing stream and resets the chat to start a fresh conversation.
   */
  protected _onNewChatRequested(): void {
    void this._chat.stop();
    this._chat.messages = [];
    // Generate a new internal chat ID so the next message creates a fresh backend session
    (this._chat as { id: string }).id = generateId();
    this._sessionCustomerId.set(null);
    this._unreadCount.set(0);
    // A fresh conversation re-asks navigation confirms
    this._skipNavConfirm.set(false);
    this._chat.clearError();
    this._errorDetails.set(null);
    this._chatSessionService.clearActiveSession();
    // Reset the title so the next message re-derives it
    this._serverTitle.set(null);
    this._titlePollStarted = false;
    this.newChatRequested.emit();
  }

  /**
   * Handles session selection from the session list.
   * Loads the session messages into the chat.
   */
  protected _onSessionSelected(sessionId: string): void {
    if (sessionId === this._chatSessionService.activeSessionId()) {
      return;
    }

    void this._chat.stop();
    this._chat.clearError();
    this._errorDetails.set(null);
    this._chatSessionService.selectSession(sessionId);

    this._chatSessionService
      .loadMessages(sessionId)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: messages => {
          this._chat.messages = messages.map(message => {
            this._messageTimestampService.setTimestamp(
              message.uuid,
              new Date(message.timestamp),
            );

            return {
              id: message.uuid,
              role: message.role === MessageRole.User ? 'user' : 'assistant',
              // Faithful reload: re-render the message from its persisted full
              // parts (text + interactive data parts). Rows written before the
              // parts column fall back to the plain-text content.
              parts:
                message.parts && message.parts.length > 0
                  ? [...message.parts]
                  : [{ type: 'text' as const, text: message.content }],
              ...(message.displayLabel
                ? { metadata: { displayLabel: message.displayLabel } }
                : {}),
            };
          });

          // Restore agent mode if available
          const modeStr = messages[0]?.mode as string;

          if (modeStr && this._agents().some(a => a.name === modeStr)) {
            this._selectedAgentName.set(modeStr);
          }

          // Use the session's actual customerId, not the current context
          const session = this._chatSessionService
            .sessions()
            .find(s => s.id === sessionId);

          this._sessionCustomerId.set(session?.customerId ?? null);
          this._unreadCount.set(0);

          // Prefer the session's stored (LLM) title; otherwise the derived
          // placeholder (from the loaded first message) applies on its own.
          if (session?.title) {
            this._serverTitle.set(session.title);
          }
        },
      });
  }

  /**
   * Handles session deletion from the session list.
   */
  protected _onSessionDeleted(sessionId: string): void {
    const isActive = this._chatSessionService.activeSessionId() === sessionId;

    this._chatSessionService.deleteSession(sessionId);

    if (isActive) {
      this._chat.messages = [];
      this._unreadCount.set(0);
      this._sessionCustomerId.set(null);
    }
  }

  /**
   * Handles enabled tools change from the input component.
   */
  protected _onEnabledToolsChange(toolIds: string[]): void {
    this._enabledTools.set(toolIds);
  }

  /**
   * Handles debug mode toggle from the input component.
   */
  protected _onDebugChange(enabled: boolean): void {
    this._debugEnabled.set(enabled);
  }

  /**
   * Opens what MYRE remembers about the connected user, for them to read,
   * correct or erase. Their memory, in their hands.
   */
  protected _onMemoryRequested(): void {
    this._agentMemoryModalService.open(
      this._chatContextService.context().params.customerId,
    );
  }

  /**
   * Sets the selected agent from the dropdown selector.
   */
  protected _setAgent(agentName: string): void {
    this._selectedAgentName.set(agentName);
  }

  /**
   * Handles retry request from the error message.
   * Clears the error and regenerates the last assistant response.
   */
  protected _onRetry(): void {
    this._chat.clearError();
    this._errorDetails.set(null);
    void this._chat.regenerate();
  }

  /**
   * Removes/disables the current context.
   */
  protected _onRemoveContext(): void {
    this._chatContextService.disable();
  }

  /**
   * Removes a single id from the context, leaving the others in scope.
   */
  protected _onRemoveContextItem(key: string): void {
    this._chatContextService.excludeKey(key);
  }

  /**
   * Unfolds the ancestors of the page's own entity, or folds them back.
   */
  protected _onToggleContext(): void {
    const page = this._chatContextService.pageKey();

    this._unfoldedPage.update(unfolded => (unfolded === page ? null : page));
  }

  /**
   * Handles errors from the AI SDK.
   */
  private _handleError(error: Error): void {
    this._abstractChatAnalyticsService?.trackResponseFailed({
      agent: this._selectedAgent()?.name,
    });

    // The SDK hands the failure back through a callback, so nothing throws and
    // the app's ErrorHandler — the one that reports to Sentry — never sees it.
    // Without this line a turn that dies on the server shows the user a
    // message and leaves no trace anywhere else.
    this._errorHandler.handleError(error);

    this._errorDetails.set(error.message || null);
    this._messageTimestampService.setTimestamp(this._errorMessageId);
  }

  /**
   * Custom fetch that uploads files separately before sending the chat message.
   * 1. For each pending file, get a presigned S3 URL via GET /files/upload-url
   * 2. Upload directly to S3 using the presigned URL
   * 3. Validate each upload via POST /files/upload-validate
   * 4. Inject the returned ChatFileAttachment[] into the JSON body
   * 5. Add file parts to the user message for display in chat history
   */
  private async _fetchWithFiles(
    url: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> {
    const files = this._pendingFiles();

    if (!files.length) {
      return globalThis.fetch(url, init);
    }

    this._pendingFiles.set([]);

    const fileAttachments: ChatFileAttachment[] = await Promise.all(
      files.map(file => this._uploadFile(file)),
    );

    this._addFilePartsToLastUserMessage(fileAttachments);

    const body = JSON.parse(init?.body as string);

    body.files = fileAttachments;

    return globalThis.fetch(url, {
      ...init,
      body: JSON.stringify(body),
    });
  }

  private async _uploadFile(file: File): Promise<ChatFileAttachment> {
    const ext = file.name.includes('.') ? file.name.split('.').pop() : '';
    const contentType = file.type || 'application/octet-stream';
    const customerId = this._chatContextService.context().params.customerId;

    // 1. Get presigned upload URL
    const uploadUrl = await firstValueFrom(
      this._fileApi.getUploadUrl({ contentType, customerId, ext }),
    );

    // 2. Upload directly to S3
    const putResponse = await globalThis.fetch(uploadUrl.url, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: file,
    });

    if (!putResponse.ok) {
      throw new Error(`S3 upload failed: ${putResponse.statusText}`);
    }

    // 3. Validate upload and set metadata
    return firstValueFrom(
      this._fileApi.validateUpload(
        { customerId },
        {
          fileId: uploadUrl.fileId,
          fileName: file.name,
        },
      ),
    );
  }

  /**
   * Stores file parts in the display-only map so uploaded files
   * are visible in the chat history without mutating _chat.messages.
   */
  private _addFilePartsToLastUserMessage(
    fileAttachments: ChatFileAttachment[],
  ): void {
    const lastUserMessage = [...this._chat.messages]
      .reverse()
      .find(m => m.role === 'user');

    if (!lastUserMessage) {
      return;
    }

    const customerId = this._chatContextService.context().params.customerId;

    const fileParts: FileUIPart[] = fileAttachments.map(attachment => ({
      type: 'file' as const,
      mediaType: attachment.mimeType,
      filename: attachment.fileName,
      url: `/customers/${customerId}/files/${attachment.fileId}`,
    }));

    this._filePartsMap.update(map => {
      const updated = new Map(map);
      const existing = updated.get(lastUserMessage.id) ?? [];
      updated.set(lastUserMessage.id, [...existing, ...fileParts]);

      return updated;
    });
  }

  private _truncateTitle(text: string): string {
    return text.length > 40 ? `${text.substring(0, 37)}...` : text;
  }

  /**
   * Polls the server for the LLM title it generates in the background for a
   * new session, replacing the locally-truncated placeholder once it lands.
   */
  private _pullServerTitle(): void {
    const sessionId = this._chat.id;

    // The session row is persisted only once the first message write lands, so
    // an immediate poll races that write and 400s. Begin at 1500ms (and repeat
    // every 1500ms) so the first request reaches an already-saved session.
    timer(1500, 1500)
      .pipe(
        switchMap(() =>
          this._chatSessionService
            .getSession(sessionId)
            .pipe(catchError(() => of(undefined))),
        ),
        map(session => session?.title?.trim()),
        filter((title): title is string => !!title),
        take(1),
        takeUntil(timer(12000)),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe(title => this._serverTitle.set(title));
  }
}
